import { Box, Grid, List, ListItemText, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import BreezeStaticIPsModal from "../components/BreezeStaticIPsModal";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import akamai1 from "./images/akamai_1.png";
import akamai2 from "./images/akamai_2.png";
import akamai3 from "./images/akamai_3.png";
import akamai4 from "./images/akamai_4.png";
import akamai5 from "./images/akamai_5.png";
import akamai6 from "./images/akamai_6.png";
import akamai7 from "./images/akamai_7.png";
import akamai8 from "./images/akamai_8.png";
import akamai9 from "./images/akamai_9.png";

import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import useStaticIPs from "../../../../api/hooks/useStaticIPs";
import { integrationKeys } from "../../../../api/queryKeys";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import TextWithExternalLink from "../../../../components/ExternalLink";
import { logos } from "../../environment/icons/iconExport";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import AkamaiWAFConnectForm from "./AkamaiWAFConnectForm";

const excludedAPIs = [
  "Akamai Marketplace",
  "Akamai University",
  "Billing",
  "Contracts-API_Contracts",
  "Contracts-API_Reporting Groups",
  "CPcode and Reporting group (cprg)",
  "Test Center",
];

const AkamaiWAFIntegration = () => {
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("akamai_waf");
  const [loading, setLoading] = useState(false);
  const [showStaticIPsModal, setShowStaticIPsModal] = useState(false);

  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerAkamaiWAFIntegration } = useAPI();
  const { staticIPs: allowlistIps } = useStaticIPs();

  const onSubmit = (values) => {
    setLoading(true);
    registerAkamaiWAFIntegration(values.credentials)
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("Akamai WAF integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? "Once integrated, the Breeze platform will automatically read the configurations from Akamai WAF and prioritize the suggested remediations."
            : ""
        }
        image={logos.AkamaiWAFLogo}
        name={"Akamai WAF"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>Akamai WAF is integrated!</Typography>
            </Box>
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}
                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton
                      onClick={() => setShowStaticIPsModal(true)}
                    >
                      View Breeze Static IPs
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Create an API Client">
                  <Typography variant={"body2"}>
                    Login to Akamai console with an admin user.
                  </Typography>
                  <TextWithExternalLink
                    text="Enter the "
                    linkText="API Client creation page"
                    href="https://control.akamai.com/apps/identity-management/#/tabs/users/createClient"
                  />
                  <Typography variant={"body2"}>
                    {"(Identity & access → Create API client)"}
                  </Typography>
                  <Typography variant={"body2"}>
                    Select <strong>Service account</strong> and click the{" "}
                    <strong>Set API client options</strong> button.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: akamai1,
                        altext: "API Client creation page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />

                  <Typography variant={"body2"}>
                    Enter the details for the new API client.
                  </Typography>
                  <Typography variant={"body2"}>
                    If IP restriction is needed, enable{" "}
                    <strong>IP Allowlist</strong> with all Breeze platform IP
                    addresses:{" "}
                    {allowlistIps.map((ip, index) => (
                      <Box
                        key={index}
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{
                          margin: 0,
                          padding: "4px 0",
                        }}
                      >
                        <span>{ip}</span>
                        <CustomCopyButton
                          copyTitle={"IP Address"}
                          copyText={ip}
                        />
                      </Box>
                    ))}
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: akamai2,
                        altext: "IP Allowlist",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Click the <strong>Select APIs</strong> button.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: akamai3,
                        altext: "Select APIs",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    In the <strong>API selection</strong> list, select READ
                    access level for all the API types, <strong>except</strong>{" "}
                    the 7 following:
                    <List>
                      {excludedAPIs.map((api) => (
                        <ListItemText
                          primaryTypographyProps={{ variant: "body2" }}
                        >
                          {api}
                        </ListItemText>
                      ))}
                    </List>
                  </Typography>
                  <Typography variant={"body2"}>
                    Then, submit the changes.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: akamai4,
                        altext: "Select Read access",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Click the <strong>Select groups</strong> button.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: akamai5,
                        altext: "Select groups",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    In the <strong>Group selection</strong> table, set{" "}
                    <strong>Viewer</strong> role to your groups and submit.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: akamai6,
                        altext: "Select Read access",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Check the review and acknowledgment checkbox and click the{" "}
                    <strong>Create API client</strong> button.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: akamai7,
                        altext: "Create API client button",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Create credentials">
                  <Typography variant={"body2"}>
                    Click the <strong>Create credentials</strong> button in the
                    credentials table.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: akamai8,
                        altext: "Create credentials",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Paste the credentials">
                  <Typography variant={"body2"}>
                    Click <strong>Copy credential</strong> and paste here.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: akamai9,
                        altext: "Copy credential",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Box width={700}>
                    <AkamaiWAFConnectForm
                      onSubmit={onSubmit}
                      guideMode={guideMode}
                    />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
      <BreezeStaticIPsModal
        open={showStaticIPsModal}
        onClose={() => setShowStaticIPsModal(false)}
      />
    </Box>
  );
};

export default AkamaiWAFIntegration;
